import axios from 'axios';
import getEnvironmentConfig from '../config/environment';
import { signedFetch } from '../utils/awsSignature';
import { fetchAuthSession } from 'aws-amplify/auth';

// Get API Gateway endpoints from environment configuration
const config = getEnvironmentConfig();
const DOC_CREATION_API_BASE = config.aws_cloud_logic_custom.find(api => api.name === 'DocCreationAPI')?.endpoint;
const AI_DRAFTING_API_BASE = config.aws_cloud_logic_custom.find(api => api.name === 'AIDraftingAPI')?.endpoint;

if (!DOC_CREATION_API_BASE || !AI_DRAFTING_API_BASE) {
  console.error('Failed to get API endpoints from environment config', {
    DOC_CREATION_API_BASE,
    AI_DRAFTING_API_BASE,
    config
  });
}

console.log('Document Service using API endpoints:', {
  hostname: window.location.hostname,
  DOC_CREATION_API_BASE,
  AI_DRAFTING_API_BASE,
  environment: config.environment,
  config: config
});

const LAMBDA_FUNCTION_URL = 'https://4wz5rvi2fzl74yl74acjohjm7m0fazbj.lambda-url.us-east-2.on.aws/';
console.log('Current environment:', getEnvironmentConfig().environment);

// Template mappings - Updated with all document types
const TEMPLATE_PATHS = {
  // Lowercase versions
  'will': 'wills/will-pourover.docx',
  'pourover_will': 'wills/will-pourover.docx',
  'revocable_trust': 'trusts/trust-disclaimer.docx',
  'disclaimer_trust': 'trusts/trust-disclaimer.docx',
  'trust': 'trusts/trust-disclaimer.docx',
  'operating_agreement': 'business/operating-agreement.docx',
  'power_of_attorney': 'powers/power-of-attorney-general.docx',
  'financial_power_of_attorney': 'powers/power-of-attorney-financial.docx',
  'healthcare_power_of_attorney': 'powers/power-of-attorney-healthcare.docx',
  'hipaa_authorization': 'authorizations/HIPAA.docx',
  'advance_directive': 'healthcare/advance-directive.docx',
  
  // Title Case versions (as returned by detectDocumentType)
  'Will': 'wills/will-pourover.docx',
  'Trust': 'trusts/trust-disclaimer.docx',
  'Financial Power of Attorney': 'powers/power-of-attorney-financial.docx',
  'Healthcare Power of Attorney': 'powers/power-of-attorney-healthcare.docx',
  'HIPAA Authorization': 'authorizations/HIPAA.docx',
  'Advance Directive': 'healthcare/advance-directive.docx',
  'Operating Agreement': 'business/operating-agreement.docx',
  'Power of Attorney': 'powers/power-of-attorney-general.docx',
  
  // Package types
  'Disclaimer Package': '', // Empty for packages - backend will determine this
  'Fractional QTIP Package': '', // Empty for packages - backend will determine this
  'Probate Avoidance Package': '', // Empty for packages - backend will determine this
  'Healthcare Documents Package': '', // Empty for packages
};

export const apiClient = axios.create({
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity
});

apiClient.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      if (idToken) {
        config.headers.Authorization = `Bearer ${idToken}`;
      }
    } catch (e) {
      // Not signed in; let the request proceed and API will 401 as appropriate
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for consistent error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} from ${response.config.url}`);
    return response;
  },
  (error) => {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.error('Request timeout:', error.config?.url);
      error.isTimeout = true;
    }
    return Promise.reject(error);
  }
);

/**
 * Orchestrate document creation through the proxy lambda
 * Enhanced to properly handle packages of documents and timeouts
 *
 * IMPORTANT: For full estate packages (Disclaimer, Fractional QTIP, Probate Avoidance),
 * this function routes to Lambda Function URL during the GENERATION phase (when
 * isSkippingPlaceholders=true) to avoid API Gateway's 29-second timeout.
 */
export async function orchestrateDocument(
  userMessage,
  documentType,
  sessionId,
  conversationStateJson = null,
  isSkippingPlaceholders = false,
  userSub = null,
  firmId = null
) {
  try {
    console.log('Orchestrating document:', {
      documentType,
      sessionId,
      isSkippingPlaceholders
    });

    // Determine if this is a package request
    const isPackage =
      documentType === 'Disclaimer Package' ||
      documentType === 'Healthcare Documents Package' ||
      documentType === 'Fractional QTIP Package' ||
      documentType === 'Probate Avoidance Package' ||
      (typeof documentType === 'string' &&
      (documentType.toLowerCase().includes('package') ||
       (documentType.toLowerCase().includes('will') &&
        documentType.toLowerCase().includes('trust') &&
        !documentType.toLowerCase().includes('or'))));

    // Full estate packages that require Lambda Function URL (take 75+ seconds)
    // Healthcare Documents Package is fast enough for API Gateway
    const fullEstatePackages = ['Disclaimer Package', 'Fractional QTIP Package', 'Probate Avoidance Package'];
    const isFullEstatePackage = fullEstatePackages.includes(documentType);

    console.log('Is package request:', isPackage);
    console.log('Is full estate package:', isFullEstatePackage);

    // Get template path based on document type
    let templatePath = '';
    if (TEMPLATE_PATHS.hasOwnProperty(documentType)) {
      templatePath = TEMPLATE_PATHS[documentType];
    } else if (!isPackage) {
      // For unknown document types, try to construct a path
      const normalizedType = documentType.toLowerCase().replace(/\s+/g, '-');
      templatePath = `documents/${normalizedType}.docx`;
      console.warn(`Unknown document type "${documentType}", using guessed path: ${templatePath}`);
    }

    // Build the payload
    const payload = {
      userMessage,
      documentType: documentType,
      sessionId: sessionId || `session-${Date.now()}`,
      templatePath,
      isPackage,
      skipRemainingPlaceholders: isSkippingPlaceholders
    };

    // Add conversationState if provided
    if (conversationStateJson) {
      payload.conversationStateJson = conversationStateJson;
    }


    let data;

    // For full estate packages during generation phase (isSkippingPlaceholders=true),
    // use Lambda Function URL to bypass API Gateway's 29-second timeout
    if (isFullEstatePackage && isSkippingPlaceholders) {
      console.log('=== ORCHESTRATION: USING LAMBDA FUNCTION URL FOR PACKAGE GENERATION ===');
      console.log('Package type:', documentType);
      console.log('Lambda URL:', LAMBDA_FUNCTION_URL);

      // Verify Lambda URL is configured
      if (!LAMBDA_FUNCTION_URL || LAMBDA_FUNCTION_URL.includes('YOUR-FUNCTION-URL-HERE')) {
        console.error('Full estate package requires Lambda Function URL to be configured');
        throw new Error(`${documentType} generation requires Lambda Function URL (bypasses 29s API Gateway timeout). Please contact support.`);
      }

      // Add action flag so backend knows this is an orchestration call
      const lambdaPayload = {
        ...payload,
        action: 'orchestrate'
      };


      // Create an AbortController for timeout handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('Lambda Function URL request timed out after 300 seconds');
        controller.abort();
      }, 300000); // 5 minute timeout

      const startTime = Date.now();

      try {
        const response = await signedFetch(LAMBDA_FUNCTION_URL, {
          method: 'POST',
          body: JSON.stringify(lambdaPayload),
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        const elapsedTime = Date.now() - startTime;
        console.log(`Lambda Function URL responded after ${elapsedTime}ms (${(elapsedTime/1000).toFixed(1)}s)`);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Lambda Function URL error:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
            elapsedTime: `${elapsedTime}ms`
          });
          throw new Error(`Lambda Function URL returned ${response.status}: ${errorText}`);
        }

        const responseData = await response.json();

        // Parse the response based on its format
        if (responseData.statusCode && responseData.body) {
          // Response is wrapped in API Gateway format
          data = typeof responseData.body === 'string'
            ? JSON.parse(responseData.body)
            : responseData.body;
        } else {
          // Direct response
          data = responseData;
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        const elapsedTime = Date.now() - startTime;

        if (fetchError.name === 'AbortError') {
          console.error(`Lambda Function URL request aborted after ${elapsedTime}ms (${(elapsedTime/1000).toFixed(1)}s)`);
          throw new Error('Package generation timed out after 5 minutes. The package may still be processing.');
        }

        console.error('Error fetching from Lambda Function URL:', fetchError);
        throw fetchError;
      }
    } else {
      // Use API Gateway for:
      // - Initial placeholder collection (fast operation)
      // - Single documents
      // - Healthcare Documents Package (fast enough for API Gateway)
      console.log('=== ORCHESTRATION: USING API GATEWAY ===');
      console.log('Reason:', isFullEstatePackage ? 'Placeholder collection phase' : 'Single doc or fast package');

      const timeoutMs = isPackage ? 90000 : 45000; // 90s for packages, 45s for single docs

      const response = await apiClient.post(
        `${DOC_CREATION_API_BASE}/doccreation/agent`,
        payload,
        { timeout: timeoutMs }
      );

      data = response.data;
    }

    
    // Parse the nested Lambda response structure
    let parsedData = {};
    
    // Check if this is a wrapped response with statusCode and body
    if (data.statusCode && data.body && typeof data.body === 'string') {
      console.log('Detected wrapped response with statusCode and body (JSON string)');
      try {
        parsedData = JSON.parse(data.body);
      } catch (parseErr) {
        console.error('Error parsing body JSON string:', parseErr);
        parsedData = data; // Fallback to original data
      }
    }
    // Check if this is a Bedrock Agent response format
    else if (data.messageVersion && data.response && data.response.responseBody) {
      console.log('Detected Bedrock Agent response format');
      const responseBody = data.response.responseBody;
      
      // Extract the actual data from the nested structure
      if (responseBody['application/json']) {
        parsedData = responseBody['application/json'];
      } else {
        console.warn('No application/json in responseBody');
        parsedData = data;
      }
    } else {
      // Direct response format (for backward compatibility)
      parsedData = data;
    }
    
    // Debug the parsed response structure
    console.log('=== PARSED RESPONSE DEBUG ===');
    console.log('parsedData.message exists?', !!parsedData.message);
    console.log('parsedData.message length:', parsedData.message?.length);
    console.log('parsedData.assistantMessage exists?', !!parsedData.assistantMessage);
    console.log('parsedData.assistantMessage length:', parsedData.assistantMessage?.length);
    
    // Get the message from the correct field
    const fullMessage = parsedData.message || parsedData.assistantMessage || '';
    if (fullMessage) {
      console.log('Full message preview:', fullMessage.substring(0, 300));
      console.log('Contains CSV delimiter?', fullMessage.includes('---- Copy the table below to a spreadsheet ----'));
    }
    console.log('=== END PARSED DEBUG ===');
    
    // Handle package responses
    if (isPackage) {
      let packageUrls = parsedData.packageUrls || {};
      
      // If no packageUrls but we have a response indicating success, construct them
      if (Object.keys(packageUrls).length === 0 && parsedData.documentKey) {
        console.log('Constructing package URLs from response');
        
        // Try to extract from the full message
        const urlMatches = fullMessage.match(/\/download\/[\w-]+\.docx/g);
        
        if (urlMatches && urlMatches.length > 0) {
          // Map URLs to document types based on filename
          urlMatches.forEach(url => {
            if (url.includes('will-')) packageUrls['Will'] = url;
            else if (url.includes('trust-')) packageUrls['Trust'] = url;
            else if (url.includes('financial')) packageUrls['Financial Power of Attorney'] = url;
            else if (url.includes('healthcare')) packageUrls['Healthcare Power of Attorney'] = url;
            else if (url.includes('hipaa') || url.includes('HIPAA')) packageUrls['HIPAA Authorization'] = url;
          });
        }
      }
      
      return {
        assistantMessage: fullMessage || "Your document package is being prepared.",
        sessionId: parsedData.sessionId || sessionId,
        documentKey: parsedData.documentKey || parsedData.docKey || null,
        documentUrl: parsedData.documentUrl || parsedData.docUrl || null,
        packageUrls: packageUrls,
        isPackage: true,
        conversationState: parsedData.conversationState || null,
        missingPlaceholders: parsedData.missingPlaceholders || []
      };
    }
    
    // For single documents
    console.log('=== RETURNING FROM ORCHESTRATE DOCUMENT ===');
    console.log('Returning object:', {
      assistantMessage: fullMessage || "Your document is being prepared.",
      sessionId: parsedData.sessionId || sessionId,
      documentKey: parsedData.documentKey || parsedData.docKey || null,
      documentUrl: parsedData.documentUrl || parsedData.docUrl || null,
      conversationState: parsedData.conversationState || null,
      missingPlaceholders: parsedData.missingPlaceholders || [],
      isPackage: false
    });
    console.log('==========================================');
    
    return {
      assistantMessage: fullMessage || "Your document is being prepared.",
      sessionId: parsedData.sessionId || sessionId,
      documentKey: parsedData.documentKey || parsedData.docKey || null,
      documentUrl: parsedData.documentUrl || parsedData.docUrl || null,
      conversationState: parsedData.conversationState || null,
      missingPlaceholders: parsedData.missingPlaceholders || [],
      isPackage: false
    };
    
  } catch (error) {
    console.error('Error orchestrating document:', error);
    
    // Enhanced error handling
    if (error.isTimeout) {
      return {
        error: "The document creation is taking longer than expected. This can happen with complex packages. Please try again.",
        assistantMessage: "The document service is processing your request. For package documents, this may take a moment. Would you like me to try again?"
      };
    }
    
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data;
      
      console.error('API Error Response:', {
        status,
        data: errorData,
        headers: error.response.headers
      });
      
      // Handle specific error cases
      if (status === 404) {
        return {
          error: "Document service endpoint not found",
          assistantMessage: "I'm having trouble connecting to the document service. Please try again in a moment."
        };
      } else if (status === 500 || status === 502 || status === 503) {
        return {
          error: "Document service temporarily unavailable",
          assistantMessage: "The document service is temporarily unavailable. Please try again in a few moments."
        };
      } else if (errorData?.error?.includes('ResourceNotFoundException')) {
        return {
          error: "Configuration error in document service",
          assistantMessage: "There's a configuration issue with the document service. Our team has been notified."
        };
      }
    }
    
    // Generic error response
    return {
      error: error.message || "Unknown error occurred",
      assistantMessage: "I encountered an error while creating your document. Please try again or contact support if the issue persists."
    };
  }
}

/**
 * Call AI drafting service for document editing
 * Enhanced error handling and package support
 */
export async function callAiDrafting({ 
  docKey, 
  prompt, 
  sessionId, 
  docType, 
  articleId,
  isPackage = false,
  conversationState = null,
  isSkippingPlaceholders = false
}) {
  try {
    if (!docKey || !prompt) {
      console.error('Missing required parameters for AI drafting');
      return { 
        error: 'Missing required parameters',
        need_more_info: 'Please provide both document key and your request'
      };
    }
    
    const apiUrl = `${AI_DRAFTING_API_BASE}/draft/${docKey}`;
    
    const payload = {
      prompt,
      inputText: prompt,
      sessionId: sessionId || null,
      docType: docType || null,
      isPackage: isPackage,
      skipRemainingPlaceholders: isSkippingPlaceholders
    };
    
    if (articleId) payload.articleId = articleId;
    if (conversationState) payload.conversationState = conversationState;
    
    console.log('AI Drafting request:', {
      url: apiUrl,
      payload: JSON.stringify(payload, null, 2)
    });
    
    const response = await apiClient.post(apiUrl, payload, {
      timeout: isPackage ? 45000 : 30000 // Longer timeout for packages
    });
    
    const data = response.data;
    
    // Handle various response formats
    if (data.need_more_info) {
      return {
        articleId: data.articleId || articleId || "article-i",
        need_more_info: data.need_more_info,
        sessionId: data.sessionId || sessionId
      };
    }
    
    if (data.instructions && Array.isArray(data.instructions)) {
      const needInfo = data.instructions.find(i => i.action === 'NEED_MORE_INFO');
      if (needInfo) {
        return {
          articleId: articleId || data.articleId || "article-i",
          need_more_info: needInfo.reason || needInfo.text || "Additional information needed",
          sessionId: data.sessionId || sessionId
        };
      }
    }
    
    if (data.articleId && data.revisedHtml && typeof data.revisedHtml === 'string') {
      return {
        articleId: data.articleId,
        revisedHtml: data.revisedHtml,
        sessionId: data.sessionId || sessionId,
        success: true
      };
    }
    
    if (data.success === true || (data.message && data.message.toLowerCase().includes("applied"))) {
      return {
        success: true,
        docKey: data.docKey || docKey,
        articleId: data.articleId || articleId || "article-i",
        message: data.message || "Changes applied successfully",
        sessionId: data.sessionId || sessionId
      };
    }
    
    // Handle assistantMessage responses from the proxy
    if (data.assistantMessage) {
      
      // Check if the message is asking for more information
      const message = data.assistantMessage;
      const needsInfo = message.toLowerCase().includes('please specify') || 
                       message.toLowerCase().includes('need more information') ||
                       message.toLowerCase().includes('could you please') ||
                       message.toLowerCase().includes('please clarify');
      
      // Extract actual document changes if present
      let actualChanges = null;
      if (data.instructions && Array.isArray(data.instructions) && data.instructions.length > 0) {
        const instruction = data.instructions[0];
        if (instruction.content) {
          actualChanges = instruction.content;
          console.log('Found actual changes in instructions:', actualChanges);
        }
      }
      
      return {
        success: true,
        message: message,
        sessionId: data.sessionId || sessionId,
        docKey: data.docKey || data.documentKey || docKey,
        docUrl: data.docUrl || data.processedDocumentKey ? `/download/${data.processedDocumentKey || data.docKey || docKey}` : null,
        need_more_info: needsInfo ? message : null,
        actualChanges: actualChanges,
        instructions: data.instructions || null
      };
    }
    
    // Handle direct message response (different field name from proxy)
    if (data.message && !data.assistantMessage) {
      
      // Check if the message is asking for more information
      const message = data.message;
      const needsInfo = message.toLowerCase().includes('please specify') || 
                       message.toLowerCase().includes('need more information') ||
                       message.toLowerCase().includes('could you please') ||
                       message.toLowerCase().includes('please clarify');
      
      // Extract actual document changes if present
      let actualChanges = null;
      if (data.instructions && Array.isArray(data.instructions) && data.instructions.length > 0) {
        const instruction = data.instructions[0];
        if (instruction.content) {
          actualChanges = instruction.content;
          console.log('Found actual changes in instructions:', actualChanges);
        }
      }
      
      return {
        success: true,
        message: message,
        assistantMessage: message, // Add this for compatibility
        sessionId: data.sessionId || sessionId,
        docKey: data.docKey || data.documentKey || data.processedDocumentKey || docKey,
        docUrl: data.docUrl || data.processedDocumentKey ? `/download/${data.processedDocumentKey || data.docKey || docKey}` : null,
        need_more_info: needsInfo ? message : null,
        actualChanges: actualChanges,
        instructions: data.instructions || null
      };
    }
    
    // Handle direct instructions response (when backend returns instructions without wrapper)
    if (data.instructions && Array.isArray(data.instructions) && data.instructions.length > 0) {
      console.log('Handling direct instructions response');
      return {
        success: true,
        instructions: data.instructions,
        sessionId: data.sessionId || sessionId,
        docKey: data.docKey || data.documentKey || data.processedDocumentKey || docKey,
        docUrl: data.docUrl || data.processedDocumentKey ? `/download/${data.processedDocumentKey || data.docKey || docKey}` : null,
        articleId: data.articleId || articleId
      };
    }
    
    // If we get here, log the unexpected response
    console.warn('Unexpected AI drafting response format:', data);
    
    // Return the raw data with instructions if present
    return {
      ...data,
      sessionId: data.sessionId || sessionId,
      message: "Request processed but response format was unexpected"
    };
    
  } catch (error) {
    console.error('Error calling AI Drafting API:', error);
    
    if (error.isTimeout) {
      return {
        error: "The editing request timed out. For complex changes, try breaking them into smaller requests."
      };
    }
    
    if (error.response?.status === 404) {
      return {
        error: "Document not found. It may have expired or been deleted."
      };
    }
    
    return { 
      error: error.message || "Failed to process editing request"
    };
  }
}

/**
 * View document as HTML
 * Enhanced error handling
 */
export async function viewDocumentAsHtml(documentKey) {
  try {
    if (!documentKey) {
      console.error('No document key provided for HTML view');
      return null;
    }
    
    console.log('Requesting HTML preview for document:', documentKey);
    
    const payload = {
      operation: 'view_as_html',
      documentKey
    };
    
    const response = await apiClient.post(
      `${AI_DRAFTING_API_BASE}/docProcessing`, 
      payload,
      { timeout: 20000 }
    );
    
    const data = response.data;
    
    // Handle direct response
    if (data && data.html) {
      return { 
        html: data.html, 
        messages: data.messages || [],
        documentKey: documentKey
      };
    }
    
    // Handle wrapped response
    if (data?.statusCode === 200 && data?.body) {
      try {
        const body = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        if (body.html) {
          return { 
            html: body.html, 
            messages: body.messages || [],
            documentKey: documentKey
          };
        }
      } catch (parseErr) {
        console.error('Error parsing HTML view response:', parseErr);
      }
    }
    
    console.error('No HTML content in response:', data);
    return null;
    
  } catch (error) {
    console.error('Error viewing document as HTML:', error);
    
    if (error.isTimeout) {
      console.error('HTML preview timed out for document:', documentKey);
    }
    
    return null;
  }
}

/**
 * Generate document from template
 * Enhanced with better error handling
 */
export async function generateDocument({ templatePath, documentType, variableValues }) {
  try {
    if (!templatePath || !documentType) {
      console.error('Missing required parameters for document generation');
      return null;
    }
    
    const payload = {
      templatePath,
      documentType,
      variableValues: variableValues || {}
    };
    
    console.log('Generating document:', {
      templatePath,
      documentType,
      variableCount: Object.keys(variableValues || {}).length
    });
    
    const response = await apiClient.post(
      `${DOC_CREATION_API_BASE}/generateDoc`, 
      payload,
      { timeout: 25000 }
    );
    
    const data = response.data;
    
    // Handle wrapped response
    if (data?.statusCode === 200 && data?.body) {
      let body;
      try {
        body = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      } catch (parseErr) {
        console.error('Error parsing generateDocument response:', parseErr);
        return null;
      }
      
      return {
        documentUrl: body.presignedUrl || body.documentUrl || null,
        presignedUrl: body.presignedUrl || null,
        documentKey: body.documentKey || null
      };
    }
    
    // Handle direct response
    if (data.documentUrl || data.presignedUrl || data.documentKey) {
      return {
        documentUrl: data.documentUrl || data.presignedUrl || null,
        presignedUrl: data.presignedUrl || null,
        documentKey: data.documentKey || null
      };
    }
    
    console.error('Unexpected response format from generateDocument:', data);
    return null;
    
  } catch (error) {
    console.error('Error generating document:', error);
    
    if (error.response) {
      console.error('Generation error response:', {
        status: error.response.status,
        data: error.response.data
      });
    }
    
    return null;
  }
}

/**
 * Get download URL for document or package
 * Enhanced to handle various URL formats
 */
export function downloadDocument(docKey, isPackage = false) {
  if (!docKey) return null;

  // Clean the document key if it contains a full path
  const cleanKey = docKey.split('/').pop();

  // If this is a package, use the package download endpoint
  if (isPackage) {
    return `${DOC_CREATION_API_BASE}/download-package/${cleanKey}`;
  }

  // Otherwise use the regular download endpoint
  return `${DOC_CREATION_API_BASE}/download/${cleanKey}`;
}

/**
 * Download document with authentication
 * Uses Cognito ID token for JWT authorization
 */
export async function downloadDocumentAuthenticated(docKey, isPackage = false, sessionId = null) {
  try {
    if (!docKey) {
      console.error('No document key provided for download');
      return { success: false, error: 'No document key provided' };
    }

    console.log('Starting authenticated download:', { docKey, isPackage, sessionId });
    console.log('docKey contains path separators?', docKey.includes('/'));
    console.log('DOC_CREATION_API_BASE:', DOC_CREATION_API_BASE);

    // Get the Cognito ID token
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (!idToken) {
      console.error('No ID token available - user may not be authenticated');
      return { success: false, error: 'Authentication required' };
    }

    console.log('ID token obtained, making authenticated request');

    // Build the download URL
    const downloadUrl = downloadDocument(docKey, isPackage);

    // Add sessionId as query parameter if provided
    const urlWithSession = sessionId
      ? `${downloadUrl}?sessionId=${encodeURIComponent(sessionId)}`
      : downloadUrl;

    console.log('Download URL:', urlWithSession);

    // Fetch the document with Authorization header
    const response = await fetch(urlWithSession, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
    });

    console.log('Download response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Download failed:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      return {
        success: false,
        error: `Download failed: ${response.status} ${response.statusText}`,
        statusCode: response.status
      };
    }

    // Get the file blob
    const blob = await response.blob();
    console.log('Blob received, size:', blob.size);

    // Extract filename from Content-Disposition header or use docKey
    let filename = docKey;
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 100);

    console.log('Download triggered successfully');
    return { success: true };

  } catch (error) {
    console.error('Error downloading document:', error);
    return {
      success: false,
      error: error.message || 'Failed to download document'
    };
  }
}

/**
 * Apply changes to DOCX
 * Enhanced error handling and response parsing
 */
export async function applyChangesToDocx({ htmlContent, documentKey, articleId }) {
  try {
    if (!documentKey) {
      console.error('No document key provided for applying changes');
      return null;
    }
    
    const payload = {
      operation: 'apply_doc_changes',
      documentKey
    };
    
    // Add optional parameters
    if (htmlContent) payload.htmlContent = htmlContent;
    if (articleId) payload.articleId = articleId;
    
    console.log('Applying changes to document:', {
      documentKey,
      hasHtmlContent: !!htmlContent,
      articleId
    });
    
    const response = await apiClient.post(
      `${AI_DRAFTING_API_BASE}/docProcessing`, 
      payload,
      { timeout: 25000 }
    );
    
    const data = response.data;
    
    // Handle direct response format
    if (data?.documentKey && (data?.presignedUrl || data?.docUrl)) {
      return {
        documentKey: data.documentKey,
        presignedUrl: data.presignedUrl || null,
        docUrl: data.docUrl || null,
        message: data.message || "Document updated successfully"
      };
    }
    
    // Handle API Gateway wrapped response
    if (data?.statusCode === 200 && data?.body) {
      try {
        const body = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
        if (body.documentKey) {
          return {
            documentKey: body.documentKey,
            presignedUrl: body.presignedUrl || null,
            docUrl: body.docUrl || null,
            message: body.message || "Document updated successfully"
          };
        }
      } catch (parseErr) {
        console.error('Error parsing apply changes response:', parseErr);
      }
    }
    
    console.warn('Unexpected response format from applyChangesToDocx:', data);
    return null;
    
  } catch (error) {
    console.error('Error applying changes to document:', error);
    
    if (error.isTimeout) {
      console.error('Apply changes timed out for document:', documentKey);
    }
    
    return null;
  }
}

/**
 * Extract document key from URL
 * Enhanced to handle various URL formats
 */
export function getDocumentKey(url) {
  if (!url) return null;
  
  // If it's already just a key, return it
  if (!url.includes('/')) return url;
  
  // Handle full URLs
  try {
    const urlObj = new URL(url, 'https://example.com'); // Add base for relative URLs
    const pathname = urlObj.pathname;
    
    // Extract from various path patterns
    if (pathname.includes('/download-package/')) {
      return pathname.split('/download-package/')[1];
    } else if (pathname.includes('/download/')) {
      return pathname.split('/download/')[1];
    } else {
      // Get the last segment
      const segments = pathname.split('/').filter(s => s);
      return segments[segments.length - 1];
    }
  } catch (err) {
    // Fallback for malformed URLs
    const parts = url.split('/');
    return parts[parts.length - 1];
  }
}

/**
 * Generate documents directly via Lambda Function URL
 * This bypasses API Gateway's 29-second timeout limit
 */
const generateDocumentsDirectLambda = async (params) => {
  try {
    console.log('=== generateDocumentsDirectLambda START ===');
    console.log('Lambda URL:', LAMBDA_FUNCTION_URL);
    console.log('Is package?', params.isPackage);
    console.log('Document type:', params.documentType);
    
    // Check if Lambda URL is configured
    if (!LAMBDA_FUNCTION_URL || LAMBDA_FUNCTION_URL.includes('YOUR-FUNCTION-URL-HERE')) {
      console.warn('Lambda Function URL not configured. Falling back to API Gateway (29s timeout).');
      throw new Error('Lambda Function URL not configured');
    }
    
    // Use AWS Signature V4 signed request
    console.log('Making AWS signed request to Lambda Function URL...');
    console.log('Request will wait for Lambda to complete (no timeout on Lambda URL)');
    
    const startTime = Date.now();
    
    // Create an AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error('Lambda Function URL request timed out after 300 seconds');
      controller.abort();
    }, 300000); // 5 minute timeout
    
    let response;
    try {
      response = await signedFetch(LAMBDA_FUNCTION_URL, {
        method: 'POST',
        body: JSON.stringify(params),
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      const elapsedTime = Date.now() - startTime;
      
      if (fetchError.name === 'AbortError') {
        console.error(`Lambda Function URL request aborted after ${elapsedTime}ms (${(elapsedTime/1000).toFixed(1)}s)`);
        throw new Error('Package generation timed out after 5 minutes. The package may still be processing.');
      }
      
      console.error('Error fetching from Lambda Function URL:', fetchError);
      throw fetchError;
    }
    
    const elapsedTime = Date.now() - startTime;
    console.log(`Lambda Function URL responded after ${elapsedTime}ms (${(elapsedTime/1000).toFixed(1)}s)`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Lambda Function URL error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
        elapsedTime: `${elapsedTime}ms`
      });
      throw new Error(`Lambda Function URL returned ${response.status}: ${errorText}`);
    }
    
    const responseData = await response.json();
    
    
    let parsedResponse;
    
    // Parse the response based on its format
    if (responseData.statusCode && responseData.body) {
      // Response is wrapped in API Gateway format
      const body = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
      parsedResponse = body;
    } else {
      // Direct response
      parsedResponse = responseData;
    }
    
    
    // Check for error in the response
    if (parsedResponse.error || parsedResponse.errorMessage) {
      console.error('Lambda returned an error:', parsedResponse.error || parsedResponse.errorMessage);
      throw new Error(parsedResponse.error || parsedResponse.errorMessage);
    }
    
    // Check if we have document URLs in the response
    if (parsedResponse.documentUrls || parsedResponse.packageUrls) {
      console.log('Found document URLs in Lambda response:', {
        documentUrls: parsedResponse.documentUrls,
        packageUrls: parsedResponse.packageUrls
      });
      
      // Add success and status fields if they're missing
      if (!parsedResponse.success) {
        parsedResponse.success = true;
        console.log('Added success: true to response');
      }
      if (!parsedResponse.status) {
        parsedResponse.status = 'completed';
        console.log('Added status: completed to response');
      }
      
      // Ensure both documentUrls and packageUrls are present for compatibility
      if (parsedResponse.documentUrls && !parsedResponse.packageUrls) {
        parsedResponse.packageUrls = parsedResponse.documentUrls;
      } else if (parsedResponse.packageUrls && !parsedResponse.documentUrls) {
        parsedResponse.documentUrls = parsedResponse.packageUrls;
      }
      
    } else {
      console.log('No document URLs found in Lambda response, checking for job-based response');
      
      // Check if this is a job-based response
      if (parsedResponse.jobId) {
        console.log('Lambda returned job ID for async processing:', parsedResponse.jobId);
        // Return as-is for job-based polling
      } else {
        console.warn('Lambda response has neither URLs nor jobId:', parsedResponse);
      }
    }
    
    return parsedResponse;
  } catch (error) {
    console.error('Error calling Lambda Function URL:', error);
    
    // Re-throw to allow fallback to API Gateway
    throw error;
  }
};

/**
 * Generate documents directly without agent interaction
 * Used when all placeholder values are provided
 */
export async function generateDocumentsDirect({
  documentType,
  variables,
  userMessage,
  isPackage,
  packageDocs = [],
  packageTemplatePaths = {},
  templatePath = '',
  placeholders = [],
  docMetadata = {},
  sessionId = null,
  userSub = null,
  firmId = null,
  // Opt-in: when true, the orchestrator renders the package's post-execution
  // To-Do List in addition to the core package documents. Collected via the
  // review-screen toggle; forwarded to Lambda verbatim.
  generateTodoList = false
}) {
  try {
    console.log('=== generateDocumentsDirect START ===');
    console.log('Document type:', documentType);
    console.log('Is package?', isPackage);
    console.log('LAMBDA_FUNCTION_URL:', LAMBDA_FUNCTION_URL);
    console.log('Should use Lambda URL?', isPackage && LAMBDA_FUNCTION_URL && !LAMBDA_FUNCTION_URL.includes('YOUR-FUNCTION-URL-HERE'));
    console.log('Variable count:', Object.keys(variables || {}).length);

    // Strip {{}} from variable keys before sending to backend
    const cleanVariables = {};
    if (variables) {
      Object.entries(variables).forEach(([key, value]) => {
        // Remove {{ and }} from the key
        const cleanKey = key.replace(/^\{\{|\}\}$/g, '');
        cleanVariables[cleanKey] = value;
      });
    }
    
    // Also clean the placeholders array
    const cleanPlaceholders = placeholders.map(p => p.replace(/^\{\{|\}\}$/g, ''));
    
    // Ensure proper payload construction
    const payload = {
      documentType: documentType,
      variables: cleanVariables,
      userMessage: userMessage || '',
      isPackage: isPackage || false,
      packageDocs: packageDocs,
      packageTemplatePaths: packageTemplatePaths,
      templatePath: templatePath || '',  // Ensure templatePath is included even if empty
      placeholders: cleanPlaceholders,
      docMetadata: docMetadata || {},
      sessionId: sessionId,
      generateTodoList: generateTodoList
    };

    console.log('📤 API Request documentType:', documentType);

    // Use Lambda Function URL for any document type that risks exceeding
    // API Gateway's hard 29-second timeout. Full estate packages take
    // 75+ seconds. Engagement Letter (single or two-doc package) and
    // standalone Planning Confirmation can each spike to ~32 seconds on
    // a cold container start (observed 2026-05-22) — over the gateway
    // limit, which silently drops the lambda's success response and
    // leaves the frontend with 0 documents. Routing them through the
    // Function URL avoids that failure mode.
    const slowPathDocTypes = [
      'Disclaimer Package',
      'Fractional QTIP Package',
      'Probate Avoidance Package',
      'Engagement Letter',
      'Planning Confirmation',
    ];
    const isFullEstatePackage = slowPathDocTypes.includes(documentType);

    if (isFullEstatePackage) {
      console.log('=== PACKAGE DETECTION ===');
      console.log('isPackage:', isPackage);
      console.log('documentType:', documentType);
      console.log('isFullEstatePackage:', isFullEstatePackage);
      console.log('LAMBDA_FUNCTION_URL exists?', !!LAMBDA_FUNCTION_URL);
      console.log('LAMBDA_FUNCTION_URL value:', LAMBDA_FUNCTION_URL);

      // Verify Lambda URL is configured
      if (!LAMBDA_FUNCTION_URL || LAMBDA_FUNCTION_URL.includes('YOUR-FUNCTION-URL-HERE')) {
        console.error('Full estate package requires Lambda Function URL to be configured');
        throw new Error(`${documentType} generation requires Lambda Function URL (bypasses 29s API Gateway timeout). Please contact support.`);
      }

      console.log('=== PACKAGE GENERATION: USING LAMBDA FUNCTION URL (15 MIN TIMEOUT) ===');
      console.log('Package type:', documentType);
      console.log('Variable count:', Object.keys(variables || {}).length);
      console.log('Lambda URL:', LAMBDA_FUNCTION_URL);
      console.log('NO FALLBACK TO API GATEWAY - Lambda only for packages!');

      const lambdaResponse = await generateDocumentsDirectLambda(payload);

      console.log('Lambda response received for package:', lambdaResponse);

      // Check if we have URLs in the response - this means success
      if (lambdaResponse.documentUrls || lambdaResponse.packageUrls || lambdaResponse.documentUrl) {
        console.log('Lambda succeeded with URLs, returning response directly');

        // Ensure success and status fields are present
        if (!lambdaResponse.success) lambdaResponse.success = true;
        if (!lambdaResponse.status) lambdaResponse.status = 'completed';

        return lambdaResponse;
      } else if (lambdaResponse.success || lambdaResponse.status === 'completed') {
        // Already marked as successful
        console.log('Lambda succeeded with success/completed status');
        return lambdaResponse;
      } else if (lambdaResponse.jobId) {
        // Job-based response
        console.log('Lambda returned job ID:', lambdaResponse.jobId);
        return {
          success: true,
          jobId: lambdaResponse.jobId,
          status: lambdaResponse.status || 'pending',
          message: lambdaResponse.message || 'Document generation started'
        };
      } else {
        // For packages, we don't fallback - throw error instead
        console.error('Package generation failed via Lambda Function URL:', lambdaResponse);
        throw new Error('Package generation failed. Please try again.');
      }
    }

    // For all other documents (single docs and Healthcare Documents Package)
    // These use API Gateway since they complete within 29 seconds
    console.log('=== SINGLE DOCUMENT GENERATION: USING API GATEWAY (29S TIMEOUT) ===');
    console.log('Document type:', documentType);
    console.log('Variable count:', Object.keys(variables || {}).length);
    console.log('Using API Gateway endpoint');

    const response = await apiClient.post(
      `${DOC_CREATION_API_BASE}/doccreation/generate-direct`,
      payload,
      { timeout: 90000 } // Won't matter, API Gateway will timeout at 29 seconds
    );

    const data = response.data;

    // Handle the response based on its format
    if (data.success && (data.documentUrls || data.packageUrls || data.documentUrl)) {
      return data;
    } else if (data.jobId) {
      return {
        success: true,
        jobId: data.jobId,
        status: data.status || 'pending',
        message: data.message || 'Document generation started'
      };
    }

    return {
      success: false,
      error: 'No job ID or document URLs returned from server'
    };

  } catch (error) {
    console.error('Error in direct document generation:', error);

    // Slow-path doc types (full packages + EL + PC) use Lambda Function URL only.
    // Keep this list in sync with the slowPathDocTypes constant above.
    const fullEstatePackages = [
      'Disclaimer Package',
      'Fractional QTIP Package',
      'Probate Avoidance Package',
      'Engagement Letter',
      'Planning Confirmation',
    ];
    if (fullEstatePackages.includes(documentType)) {
      console.error(`${documentType} generation error (Lambda Function URL):`, error);

      if (error.message.includes('Lambda Function URL')) {
        return {
          success: false,
          error: error.message,
          requiresConfiguration: true
        };
      }

      // For full estate packages, we only use Lambda URL, so any error is a Lambda error
      return {
        success: false,
        error: `${documentType} generation error: ${error.message || 'Unknown error occurred'}. This package requires direct Lambda invocation to avoid the 29-second API Gateway timeout.`,
        isPackageError: true
      };
    }

    // Single document error handling (API Gateway)
    if (error.response?.status === 503 || error.code === 'ECONNABORTED') {
      // This is likely the API Gateway timeout for single documents
      return {
        success: false,
        error: 'The request timed out at the API Gateway level (29 second limit).',
        isTimeout: true
      };
    }

    if (error.response) {
      return {
        success: false,
        error: error.response.data?.error || error.message
      };
    }

    return {
      success: false,
      error: error.message || 'Failed to start document generation'
    };
  }
}

/**
 * Check the status of a document generation job
 */
export async function checkJobStatus(jobId) {
  try {
    if (!jobId) {
      console.error('No job ID provided for status check');
      return { error: 'No job ID provided' };
    }

    console.log('Checking job status:', jobId);

    const response = await apiClient.get(
      `${DOC_CREATION_API_BASE}/doccreation/job-status/${jobId}`,
      { timeout: 5000 } // 5 second timeout
    );

    const data = response.data;

    return {
      jobId: data.jobId,
      status: data.status,
      timestamp: data.timestamp,
      documentUrls: data.documentUrls || {},
      error: data.error || null
    };

  } catch (error) {
    console.error('Error checking job status:', error);
    
    if (error.response?.status === 404) {
      return {
        error: 'Job not found',
        status: 'not_found'
      };
    }
    
    return {
      error: error.message || 'Failed to check job status',
      status: 'error'
    };
  }
}

/**
 * Fetch recent documents from S3
 */
export async function fetchRecentDocuments(limit = 20) {
  try {
    console.log('Fetching recent documents...');
    
    const response = await apiClient.get(
      `${AI_DRAFTING_API_BASE}/documents/recent`,
      {
        params: { limit },
        timeout: 10000
      }
    );
    
    const data = response.data;
    console.log(`Found ${data.count} recent documents`);
    
    return {
      documents: data.documents || [],
      count: data.count || 0
    };
    
  } catch (error) {
    console.error('Error fetching recent documents:', error);
    return {
      documents: [],
      count: 0,
      error: error.message
    };
  }
}

/**
 * Fetch templates from estate-plan-docs-docx bucket
 */
export async function fetchTemplatesFromBucket() {
  try {
    console.log('Fetching templates from S3...');
    
    const response = await apiClient.get(
      `${DOC_CREATION_API_BASE}/templates`,
      { timeout: 10000 }
    );
    
    const data = response.data;
    console.log(`Found ${data.count} templates`);
    
    return {
      templates: data.templates || [],
      groupedTemplates: data.groupedTemplates || {},
      count: data.count || 0
    };
    
  } catch (error) {
    console.error('Error fetching templates:', error);
    return {
      templates: [],
      groupedTemplates: {},
      count: 0,
      error: error.message
    };
  }
}

/**
 * Call AI drafting agent directly (not through document creation agent)
 * This uses the dedicated AI drafting agent MNDHV7IACN for editing documents
 */
export async function callAiDraftingAgent({ docKey, prompt, sessionId, docType, userSub, firmId }) {
  try {
    if (!docKey || !prompt) {
      console.error('Missing required parameters for AI drafting agent');
      return { 
        error: 'Missing required parameters',
        needMoreInfo: true,
        needMoreInfoReason: 'Please provide both document key and your editing request'
      };
    }
    
    // Use the AI Drafting API base URL - the proxy endpoint
    const apiUrl = `${AI_DRAFTING_API_BASE}/agent`;
    
    // Construct the user message to include the document key
    const userMessage = `Edit document ${docKey}: ${prompt}`;
    
    const payload = {
      userMessage: userMessage,
      sessionId: sessionId || `session-${Date.now()}`,
      docType: docType || 'document',
      docKey: docKey
    };
    
    console.log('=== CALLING AI DRAFTING AGENT ===');
    console.log('API URL:', apiUrl);
    console.log('Agent: MNDHV7IACN (AI Drafting Agent)');
    
    const response = await apiClient.post(apiUrl, payload, {
      timeout: 60000 // 60 second timeout for agent processing
    });
    
    const data = response.data;
    
    // Parse the response - the proxy returns various formats
    if (data.instructions && Array.isArray(data.instructions) && data.instructions.length > 0) {
      return {
        success: true,
        instructions: data.instructions,
        instructionCount: data.instructionCount || data.instructions.length,
        docKey: data.docKey || data.documentKey || docKey,
        docUrl: data.docUrl || data.documentUrl,
        processedDocumentKey: data.processedDocumentKey || data.editedDocumentKey,
        sessionId: data.sessionId || sessionId
      };
    }
    
    // Check for need more info
    if (data.needMoreInfo || data.needMoreInfoReason) {
      console.log('Agent needs more information');
      return {
        needMoreInfo: true,
        needMoreInfoReason: data.needMoreInfoReason || data.assistantMessage || 'Please provide more details about what you want to change',
        sessionId: data.sessionId || sessionId
      };
    }
    
    // Check for assistant message (general response)
    if (data.assistantMessage) {
      
      // Check if it's asking for more info based on content
      const message = data.assistantMessage.toLowerCase();
      if (message.includes('please specify') || 
          message.includes('what changes') || 
          message.includes('more details') || 
          message.includes('which section')) {
        return {
          needMoreInfo: true,
          needMoreInfoReason: data.assistantMessage,
          sessionId: data.sessionId || sessionId
        };
      }
      
      // Otherwise return as general message
      return {
        success: true,
        message: data.assistantMessage,
        assistantMessage: data.assistantMessage,
        sessionId: data.sessionId || sessionId,
        docKey: data.docKey || data.documentKey || docKey
      };
    }
    
    // Handle unexpected response
    console.warn('Unexpected response format from AI Drafting Agent:', data);
    return {
      success: false,
      error: 'Unexpected response format',
      message: 'The AI drafting service returned an unexpected response. Please try again.',
      rawResponse: data
    };
    
  } catch (error) {
    console.error('Error calling AI Drafting Agent:', error);
    
    if (error.isTimeout) {
      return {
        error: 'The AI drafting request timed out. Please try a simpler edit request.'
      };
    }
    
    if (error.response?.status === 404) {
      return {
        error: 'AI drafting service not found. Please check the configuration.'
      };
    }
    
    return { 
      error: error.message || 'Failed to process AI drafting request'
    };
  }
}

/**
 * Fetch template metadata from S3
 * Retrieves the -metadata.json file for a given template
 */
export async function fetchTemplateMetadata(templatePath) {
  try {
    if (!templatePath) {
      console.error('No template path provided for metadata fetch');
      return null;
    }
    
    console.log('Fetching metadata for template:', templatePath);
    
    // Construct metadata file path by replacing .docx with -metadata.json
    const metadataPath = templatePath.replace('.docx', '-metadata.json');
    
    const response = await apiClient.get(
      `${DOC_CREATION_API_BASE}/template-metadata`,
      {
        params: { path: metadataPath },
        timeout: 10000
      }
    );
    
    if (response.data) {
      return response.data;
    }
    
    console.warn('No metadata found for template:', templatePath);
    return null;
    
  } catch (error) {
    console.error('Error fetching template metadata:', error);
    
    // Return null instead of throwing to allow graceful fallback
    return null;
  }
}

/**
 * Fetch metadata for multiple templates
 * Useful for packages that contain multiple documents
 */
export async function fetchMultipleTemplateMetadata(templatePaths) {
  try {
    if (!Array.isArray(templatePaths) || templatePaths.length === 0) {
      console.error('Invalid template paths provided');
      return {};
    }
    
    console.log('Fetching metadata for multiple templates:', templatePaths);
    
    const metadataPromises = templatePaths.map(path => 
      fetchTemplateMetadata(path).then(metadata => ({ path, metadata }))
    );
    
    const results = await Promise.all(metadataPromises);
    
    // Convert to object keyed by template path
    const metadataMap = {};
    results.forEach(({ path, metadata }) => {
      if (metadata) {
        metadataMap[path] = metadata;
      }
    });
    
    console.log('Fetched metadata for templates:', Object.keys(metadataMap));
    return metadataMap;
    
  } catch (error) {
    console.error('Error fetching multiple template metadata:', error);
    return {};
  }
}

// Export constants for use in other files
export { DOC_CREATION_API_BASE, AI_DRAFTING_API_BASE, LAMBDA_FUNCTION_URL };
